import {
  Box,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Switch,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import { CommonDialog } from "@/components/common/CommonDialog";
import { PrimaryButton, SecondaryButton } from "@/components/common";

import { CheckoutAddressOptionCard } from "./CheckoutAddressOptionCard";
import { MarketplaceAddressFormFields } from "./MarketplaceAddressFormFields";
import { marketplaceTypography as mt } from "../constants/marketplaceTypography";
import type {
  MarketplaceAddressFormValues,
  MarketplaceAddressOption,
  MarketplaceCheckoutAddresses,
  WooCustomer,
} from "../types";
import {
  customerPrefillFormValues,
  emptyAddressFormValues,
  formatAddressDisplayLines,
  formatAddressSummary,
  isCheckoutAddressComplete,
  mergeAddressFormPrefill,
  mergeSavedAddressWithPersonalData,
  patientPersonalPrefillOnly,
  patientProfilePrefillFormValues,
  resolveDefaultSavedAddressOptionId,
} from "../utils/addressHelpers";

type ShippingSource = "saved" | "custom";

/** shipping = só entrega (Alterar entrega); billing = só cobrança (Alterar cobrança); default = fluxo entrega → passo cobrança com toggle. */
type CheckoutAddressWizardFocus = "shipping" | "billing" | "default";

interface CheckoutAddressWizardDialogProps {
  open: boolean;
  initialStep?: 1 | 2;
  wizardFocus?: CheckoutAddressWizardFocus;
  customer: WooCustomer;
  userEmail: string;
  userPatient?: unknown;
  userName?: string;
  patientCpf?: string;
  addressOptions?: MarketplaceAddressOption[];
  initialShipping?: MarketplaceAddressFormValues;
  initialBilling?: MarketplaceAddressFormValues;
  onClose: () => void;
  onComplete: (addresses: MarketplaceCheckoutAddresses) => void;
}

const STEPS = ["Entrega", "Cobrança"];

/** Wizard de endereço — etapa 1 entrega, etapa 2 cobrança. */
export function CheckoutAddressWizardDialog({
  open,
  initialStep = 1,
  wizardFocus = "default",
  customer,
  userEmail,
  userPatient,
  userName,
  patientCpf,
  addressOptions = [],
  initialShipping,
  initialBilling,
  onClose,
  onComplete,
}: CheckoutAddressWizardDialogProps) {
  const theme = useTheme();
  const hasSavedOptions = addressOptions.length > 0;
  const defaultSavedId = resolveDefaultSavedAddressOptionId(
    addressOptions,
    initialShipping,
  );

  const buildCustomFormDefaults = (
    includeAddressFields = !hasSavedOptions,
  ): MarketplaceAddressFormValues => {
    const patientPrefill = patientProfilePrefillFormValues(userPatient, {
      userName,
      patientCpf,
    });
    const customerPrefill = customerPrefillFormValues(customer);

    if (includeAddressFields) {
      return mergeAddressFormPrefill(
        patientPrefill,
        customerPrefill,
        initialShipping ?? {},
      );
    }

    return mergeAddressFormPrefill(
      patientPersonalPrefillOnly(userPatient, { userName, patientCpf }, customer),
      {
        cep: "",
        street: "",
        number: "",
        complement: "",
        neighborhood: "",
        city: "",
        state: "",
      },
    );
  };

  const [step, setStep] = useState<1 | 2>(initialStep);
  const [shippingSource, setShippingSource] = useState<ShippingSource>(
    hasSavedOptions ? "saved" : "custom",
  );
  const [selectedSavedId, setSelectedSavedId] = useState(defaultSavedId);
  const [billingSameAsShipping, setBillingSameAsShipping] = useState(true);
  const [stepError, setStepError] = useState<string | null>(null);

  const shippingForm = useForm<MarketplaceAddressFormValues>({
    defaultValues: buildCustomFormDefaults(),
  });

  const billingForm = useForm<MarketplaceAddressFormValues>({
    defaultValues:
      initialBilling ??
      mergeAddressFormPrefill(buildCustomFormDefaults(), initialShipping ?? {}),
  });

  const isBillingOnly = wizardFocus === "billing";
  const isShippingOnly = wizardFocus === "shipping";
  const showBillingStep = !isBillingOnly && !isShippingOnly;

  useEffect(() => {
    if (!open) return;
    setStep(isBillingOnly ? 2 : initialStep);
    setShippingSource(hasSavedOptions ? "saved" : "custom");
    setSelectedSavedId(
      resolveDefaultSavedAddressOptionId(addressOptions, initialShipping),
    );
    const billingMatchesShipping =
      Boolean(initialShipping && initialBilling) &&
      formatAddressSummary(initialShipping) === formatAddressSummary(initialBilling);
    setBillingSameAsShipping(!initialBilling || billingMatchesShipping);
    setStepError(null);
    shippingForm.reset(buildCustomFormDefaults());
    billingForm.reset(
      initialBilling ??
        mergeAddressFormPrefill(buildCustomFormDefaults(), initialShipping ?? {}),
    );
  }, [open, initialStep, isBillingOnly, hasSavedOptions, customer, initialShipping, initialBilling, addressOptions, userPatient, userName, patientCpf]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleShippingSourceChange = (value: string) => {
    if (value === "custom") {
      setShippingSource("custom");
      shippingForm.reset(buildCustomFormDefaults(false));
      return;
    }
    setShippingSource("saved");
    setSelectedSavedId(value);
  };

  const resolveSavedValues = (): MarketplaceAddressFormValues | null => {
    const option = addressOptions.find((opt) => opt.id === selectedSavedId);
    if (!option) return null;
    return mergeSavedAddressWithPersonalData(option, {
      userPatient,
      userName,
      patientCpf,
      customer,
    });
  };

  const resolveShippingValues = (): MarketplaceAddressFormValues => {
    if (shippingSource === "saved") {
      return resolveSavedValues() ?? emptyAddressFormValues();
    }
    return shippingForm.getValues();
  };

  const validateShipping = (): MarketplaceAddressFormValues | null => {
    const values = resolveShippingValues();
    if (!isCheckoutAddressComplete(values)) {
      setStepError(
        "Preencha nome, CPF, telefone, CEP, rua, número (ou S/N), bairro, cidade e UF para a entrega.",
      );
      return null;
    }
    setStepError(null);
    return values;
  };

  const validateBilling = (
    shipping: MarketplaceAddressFormValues,
  ): MarketplaceAddressFormValues | null => {
    if (billingSameAsShipping) return shipping;

    const values = billingForm.getValues();
    if (!isCheckoutAddressComplete(values)) {
      setStepError(
        "Informe o endereço de cobrança em Alterar, na seção Cobrança do resumo.",
      );
      return null;
    }
    setStepError(null);
    return values;
  };

  const completeWithShipping = (shipping: MarketplaceAddressFormValues) => {
    const prevShipping = initialShipping;
    const prevBilling = initialBilling ?? shipping;
    const billingTrackedSame =
      !prevShipping ||
      formatAddressSummary(prevShipping) === formatAddressSummary(prevBilling);
    const billing = billingTrackedSame ? { ...shipping } : prevBilling;
    onComplete({ shipping, billing });
  };

  const handleContinueStep1 = () => {
    if (shippingSource === "custom") {
      void shippingForm.handleSubmit((values) => {
        if (!isCheckoutAddressComplete(values)) {
          setStepError(
            "Preencha nome, CPF, telefone e todos os campos obrigatórios de entrega.",
          );
          return;
        }
        setStepError(null);
        if (isShippingOnly) {
          completeWithShipping(values);
          return;
        }
        if (billingSameAsShipping) {
          billingForm.reset(values);
        }
        if (showBillingStep) {
          setStep(2);
          return;
        }
        completeWithShipping(values);
      })();
      return;
    }

    const values = validateShipping();
    if (!values) return;
    if (isShippingOnly) {
      completeWithShipping(values);
      return;
    }
    if (billingSameAsShipping) billingForm.reset(values);
    if (showBillingStep) {
      setStep(2);
      return;
    }
    completeWithShipping(values);
  };

  const handleSaveBillingOnly = () => {
    if (!initialShipping) {
      setStepError("Salve o endereço de entrega antes da cobrança.");
      return;
    }
    void billingForm.handleSubmit((values) => {
      if (!isCheckoutAddressComplete(values)) {
        setStepError(
          "Para cobrança, informe CPF, telefone e endereço completos.",
        );
        return;
      }
      setStepError(null);
      onComplete({ shipping: initialShipping, billing: values });
    })();
  };

  const handleFinish = () => {
    const shipping = validateShipping();
    if (!shipping) {
      setStep(1);
      return;
    }
    const billing = validateBilling(shipping);
    if (!billing) return;
    onComplete({ shipping, billing });
  };

  const footer = isBillingOnly ? (
    <Stack direction={{ xs: "column-reverse", sm: "row" }} spacing={1.5} width="100%">
      <SecondaryButton width="fit-content" height="48px" onClick={onClose}>
        Voltar
      </SecondaryButton>
      <PrimaryButton
        width={{ xs: "100%", sm: "fit-content" }}
        height="48px"
        onClick={handleSaveBillingOnly}
      >
        Salvar
      </PrimaryButton>
    </Stack>
  ) : step === 1 ? (
      <Stack direction={{ xs: "column-reverse", sm: "row" }} spacing={1.5} width="100%">
        <SecondaryButton width="fit-content" height="48px" onClick={onClose}>
          Voltar
        </SecondaryButton>
        <PrimaryButton width={{ xs: "100%", sm: "fit-content" }} height="48px" onClick={handleContinueStep1}>
          Salvar
        </PrimaryButton>
      </Stack>
    ) : (
      <Stack direction={{ xs: "column-reverse", sm: "row" }} spacing={1.5} width="100%">
        <SecondaryButton width="fit-content" height="48px" onClick={() => setStep(1)}>
          Voltar
        </SecondaryButton>
        <PrimaryButton width={{ xs: "100%", sm: "fit-content" }} height="48px" onClick={handleFinish}>
          Ver resumo do pedido
        </PrimaryButton>
      </Stack>
    );

  return (
    <CommonDialog
      isOpen={open}
      handleClose={onClose}
      maxWidth="md"
      sheetTitle={
        isBillingOnly
          ? "Endereço de cobrança"
          : step === 1
            ? "Para onde enviamos?"
            : "Endereço de cobrança"
      }
      sheetSubtitle={
        isBillingOnly
          ? "Informe o endereço vinculado ao cartão ou à nota fiscal."
          : step === 1
            ? "Passo 1 de 2 — onde você quer receber os produtos."
            : "Passo 2 de 2 — confirme se a cobrança usa o mesmo endereço da entrega."
      }
      footer={footer}
      sheetSize="large"
    >
      <Stack spacing={3} sx={{ py: 0.5 }}>
        {!isBillingOnly ? (
          <Stepper
            activeStep={step - 1}
            alternativeLabel
            sx={{
              px: { xs: 0, sm: 2 },
              "& .MuiStepLabel-label": { ...mt.bodySecondary },
            }}
          >
            {STEPS.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        ) : null}

        {stepError ? (
          <Typography sx={{ ...mt.body, color: "error.main" }}>{stepError}</Typography>
        ) : null}

        {isBillingOnly ? (
          <MarketplaceAddressFormFields
            key={`billing-only-${String(open)}`}
            control={billingForm.control}
            setValue={billingForm.setValue}
            setError={billingForm.setError}
          />
        ) : step === 1 ? (
          <Stack spacing={3}>
            {hasSavedOptions ? (
              <Stack spacing={1.5}>
                <Typography sx={{ ...mt.sectionTitle, color: "primary800.main" }}>
                  Endereços salvos
                </Typography>
                <RadioGroup
                  value={shippingSource === "custom" ? "custom" : selectedSavedId}
                  onChange={(e) => handleShippingSourceChange(e.target.value)}
                >
                  <Stack spacing={1.25}>
                    {addressOptions.map((option) => (
                      <CheckoutAddressOptionCard
                        key={option.id}
                        value={option.id}
                        selected={
                          shippingSource === "saved" && selectedSavedId === option.id
                        }
                        heading={option.heading}
                        values={option.values}
                      />
                    ))}

                    <CheckoutAddressOptionCard
                      value="custom"
                      selected={shippingSource === "custom"}
                      heading="Novo endereço"
                      variant="new"
                    />
                  </Stack>
                </RadioGroup>
              </Stack>
            ) : null}

            {(!hasSavedOptions || shippingSource === "custom") ? (
              <Stack spacing={0}>
                <MarketplaceAddressFormFields
                  key={`shipping-${String(open)}-${shippingSource}`}
                  control={shippingForm.control}
                  setValue={shippingForm.setValue}
                  setError={shippingForm.setError}
                />
              </Stack>
            ) : null}
          </Stack>
        ) : (
          <Stack spacing={3}>
            <Box
              sx={{
                p: 2.5,
                borderRadius: 2,
                border: "1px solid",
                borderColor: alpha(theme.palette.primary800.main, 0.14),
                bgcolor: alpha(theme.palette.primary800.main, 0.04),
              }}
            >
              <Typography sx={{ ...mt.bodySecondary, color: "text.secondary", mb: 1 }}>
                Entrega em
              </Typography>
              {(() => {
                const lines = formatAddressDisplayLines(resolveShippingValues());
                const displayLines = [lines.streetLine, lines.cityLine, lines.cepLine].filter(
                  Boolean,
                );
                return (
                  <Stack spacing={0.35}>
                    {displayLines.map((line) => (
                      <Typography
                        key={line}
                        sx={{
                          ...mt.body,
                          fontWeight: displayLines.indexOf(line) === 0 ? 700 : 400,
                          color: displayLines.indexOf(line) === 0 ? "text.primary" : "text.secondary",
                        }}
                      >
                        {line}
                      </Typography>
                    ))}
                  </Stack>
                );
              })()}
            </Box>

            <FormControlLabel
              control={
                <Switch
                  checked={billingSameAsShipping}
                  onChange={(e) => setBillingSameAsShipping(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Typography sx={{ ...mt.body, fontWeight: 600 }}>
                  Usar o mesmo endereço de entrega para cobrança
                </Typography>
              }
              sx={{ mx: 0 }}
            />

            {!billingSameAsShipping ? (
              <Typography sx={{ ...mt.bodySecondary, color: "text.secondary" }}>
                Para informar outro endereço de cobrança, use{" "}
                <strong>Alterar</strong> na seção Cobrança do resumo do pedido.
              </Typography>
            ) : (
              <Typography sx={{ ...mt.bodySecondary, color: "text.secondary" }}>
                A cobrança usará o mesmo endereço da entrega.
              </Typography>
            )}
          </Stack>
        )}
      </Stack>
    </CommonDialog>
  );
}
